<template>
  <b-card-code
    title="Créer un nouveau Expéditeur"
    aria-label="Formulaire d’ajout de fournisseur"
    role="main"
  >
    <b-overlay
      :show="isLoading"
      rounded="sm"
      spinner-variant="primary"
      aria-busy="true"
      aria-live="polite"
    >
      <b-form
        aria-labelledby="supplier-form-title"
        role="form"
        @submit.prevent="validateAndSubmit"
      >
        <h1
          id="supplier-form-title"
          class="sr-only"
        >
          Formulaire d’ajout de fournisseur logistique
        </h1>
        <p class="sr-only">
          Tous les champs marqués d’un astérisque (*) sont obligatoires. Utilisez des informations professionnelles et logistiques précises.
        </p>
        <ValidationObserver
          ref="simpleRules"
        >
          <b-card
            class="mb-3"
            aria-label="Informations générales du fournisseur"
          >
            <h5
              id="section-infos-generales"
              class="mb-3 font-weight-bold"
            >
              <feather-icon
                icon="UserIcon"
                class="mr-1"
                aria-hidden="true"
              />
              Informations générales
            </h5>

            <b-row>
              <!-- Username -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label-for="username"
                  description="Nom commercial du fournisseur, tel qu'indiqué sur les documents officiels."
                  aria-required="true"
                >
                  <template #label>
                    Nom du expéditeur <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Nom"
                    rules="required"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-form-input
                        id="username"
                        v-model="supplier.username"
                        :state="errors.length > 0 ? false : (valid && supplier.username ? true : null)"
                        placeholder="Entrez le nom du fournisseur"
                        class="premium-input"
                      />
                      <feather-icon
                        v-if="valid && supplier.username"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <!-- Téléphone principal handled below with validation-provider -->
              </b-col>

              <!-- Type de fournisseur -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label-for="is_an_shipment_company"
                  description="Société de livraison partenaire qui nous sous-traite la livraison de ses colis et synchronise ses commandes avec notre plateforme."
                >
                  <template #label>
                    Société de livraison (Sous-traitance)
                  </template>
                  <b-form-checkbox
                    id="is_an_shipment_company"
                    v-model="supplier.is_an_shipment_company"
                    name="is_an_shipment_company"
                    switch
                    :value="true"
                    :unchecked-value="false"
                  >
                    Marquer comme société de livraison en sous-traitance
                  </b-form-checkbox>
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label-for="email"
                  description="Adresse e-mail utilisée pour la connexion et la communication interne."
                  aria-required="true"
                >
                  <template #label>
                    Adresse e-mail professionnelle <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Email"
                    rules="required|email|asyncEmail"
                    mode="eager"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        id="email"
                        v-model="supplier.email"
                        type="email"
                        :state="errors.length > 0 ? false : (valid && supplier.email ? true : null)"
                        placeholder="Entrez l'email"
                        class="premium-input"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          v-b-tooltip.hover="emailGenerationTooltip"
                          icon="RefreshCwIcon"
                          :class="supplier.username && supplier.username.trim().length > 0 ? 'cursor-pointer text-primary' : 'text-muted cursor-not-allowed'"
                          @click="supplier.username && supplier.username.trim().length > 0 ? generateEmail() : null"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <!-- Téléphone secondaire handled below with validation-provider -->
              </b-col>

              <!-- Téléphone société (unique, validated, with allowOnlyNumbers) -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label-for="phone"
                  description="Numéro de téléphone personnel (8 chiffres, sans espaces)."
                  aria-required="true"
                >
                  <template #label>
                    Téléphone personnel <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Téléphone"
                    rules="required|tnPhone|asyncPhone"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-form-input
                        id="phone"
                        v-model="supplier.phone"
                        :formatter="formatPhone"
                        :state="errors.length > 0 ? false : (valid && supplier.phone ? true : null)"
                        placeholder="Entrez le numéro de téléphone"
                        class="premium-input"
                        @keypress="allowOnlyNumbers"
                      />
                      <feather-icon
                        v-if="valid && supplier.phone"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Téléphone personnel (unique, validated, with allowOnlyNumbers) -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label="Téléphone société"
                  label-for="phone2"
                  description="Numéro de téléphone de la société (optionnel)."
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Téléphone société"
                    rules="tnPhone"
                  >
                    <b-form-input
                      id="phone2"
                      v-model="supplier.phone2"
                      class="premium-input"
                      :formatter="formatPhone"
                      :state="errors.length > 0 ? false : null"
                      placeholder="Entrez un autre numéro (optionnel)"
                      @keypress="allowOnlyNumbers"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Nom de la page (optionnel) -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label="Nom de la page (Facebook/Instagram, etc.)"
                  label-for="page_name"
                  description="Nom de la page commerciale du fournisseur (optionnel)."
                >
                  <b-form-input
                    id="page_name"
                    v-model="supplier.page_name"
                    class="premium-input"
                    placeholder="Ex: Ma Boutique Officielle"
                  />
                </b-form-group>
              </b-col>

              <!-- URL du site (optionnel) -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label="URL du site web"
                  label-for="website_url"
                  description="Adresse du site web du fournisseur (optionnel)."
                >
                  <b-form-input
                    id="website_url"
                    v-model="supplier.website_url"
                    class="premium-input"
                    type="url"
                    placeholder="https://exemple.com"
                  />
                </b-form-group>
              </b-col>

            </b-row>
          </b-card>

          <!-- Payment Configuration Card -->
          <b-card
            class="mb-3 payment-configuration-card"
            aria-label="Configuration de paiement du fournisseur"
          >
            <div class="d-flex align-items-center mb-3">
              <div class="payment-icon-wrapper">
                <feather-icon
                  icon="CreditCardIcon"
                  size="20"
                  class="text-primary"
                  aria-hidden="true"
                />
              </div>
              <div class="ml-2">
                <h5 class="mb-0 font-weight-bold">
                  Configuration de paiement
                </h5>
                <small class="text-muted">
                  Définissez les modalités de règlement et la planification des factures
                </small>
              </div>
            </div>

            <b-row>
              <!-- Payment Type -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label-for="payment_type"
                  description="Mode de paiement utilisé pour régler le fournisseur."
                  class="premium-form-group"
                >
                  <template #label>
                    Type de paiement <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors }"
                    name="Type de paiement"
                    rules="required"
                  >
                    <v-select
                      id="payment_type"
                      v-model="supplier.payment_type"
                      class="premium-input"
                      :options="paymentTypeOptions"
                      :reduce="option => option.value"
                      :clearable="false"
                      label="label"
                      placeholder="Sélectionnez un type de paiement"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- RIB (conditional) -->
              <b-col
                v-if="supplier.payment_type === 'virement'"
                md="6"
              >
                <b-form-group
                  label-class="form-label-premium"
                  label-for="rib"
                  description="RIB du fournisseur pour les paiements par virement."
                  class="premium-form-group"
                >
                  <template #label>
                    RIB
                  </template>
                  <validation-provider
                    #default="{ errors }"
                    name="RIB"
                  >
                    <b-form-input
                      id="rib"
                      v-model="supplier.rib"
                      class="premium-input"
                      :state="errors.length > 0 ? false : null"
                      placeholder="Entrez le RIB"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Invoice Generation Days -->
              <b-col
                :md="supplier.payment_type === 'virement' ? 12 : 6"
                class="mt-2"
              >
                <div class="payment-days-section">
                  <div class="d-flex align-items-center mb-2">
                    <feather-icon
                      icon="CalendarIcon"
                      size="16"
                      class="text-primary mr-1"
                    />
                    <label class="mb-0 font-weight-bold">
                      Jours de paiement (Facturation)
                    </label>
                  </div>
                  <small class="text-muted d-block mb-3">
                    Cliquez sur les jours de la semaine prévus pour le règlement et la génération automatique des factures.
                  </small>

                  <!-- Visual Day Picker -->
                  <div class="days-picker-grid">
                    <div
                      v-for="day in weekdayOptions"
                      :key="day.value"
                      :class="[
                        'day-picker-item',
                        { 'selected': isDaySelected(day.value) }
                      ]"
                      @click="toggleDay(day.value)"
                    >
                      <div class="day-icon mb-1">
                        <feather-icon
                          v-if="isDaySelected(day.value)"
                          icon="CheckCircleIcon"
                          size="24"
                          class="text-success"
                        />
                        <feather-icon
                          v-else
                          icon="CircleIcon"
                          size="24"
                          class="text-muted"
                        />
                      </div>
                      <div class="day-label">
                        {{ day.label }}
                      </div>
                    </div>
                  </div>

                  <!-- Summary -->
                  <div class="mt-3">
                    <div
                      v-if="supplier.invoice_generation_days && supplier.invoice_generation_days.length > 0"
                      class="selected-summary"
                    >
                      <small class="text-success font-weight-bold">
                        <feather-icon
                          icon="CheckIcon"
                          size="14"
                          class="mr-1"
                        />
                        {{ supplier.invoice_generation_days.length }} jour(s) sélectionné(s)
                      </small>
                    </div>
                    <div
                      v-else
                      class="text-muted"
                    >
                      <small>
                        <feather-icon
                          icon="InfoIcon"
                          size="14"
                          class="mr-1"
                        />
                        Aucun jour sélectionné (optionnel)
                      </small>
                    </div>
                  </div>
                </div>
              </b-col>

              <!-- Tax ID -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label="Matricule fiscal"
                  label-for="tax_id"
                  description="Numéro d’identification fiscale du fournisseur."
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Matricule fiscal"
                  >
                    <b-form-input
                      id="tax_id"
                      v-model="supplier.tax_identification_number"
                      class="premium-input"
                      :state="errors.length > 0 ? false : (cinOrMatriculeError ? false : null)"
                      placeholder="Entrez le matricule fiscal"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- CIN -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label="CIN"
                  label-for="cin"
                  description="Carte d'identité nationale du fournisseur (8 chiffres)."
                >
                  <validation-provider
                    #default="{ errors, valid }"
                    name="CIN"
                    rules="tnCin"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-form-input
                        id="cin"
                        v-model="supplier.cin"
                        :state="errors.length > 0 ? false : (valid && supplier.cin && supplier.cin.length === 8 ? true : null)"
                        placeholder="Entrez le CIN (8 chiffres)"
                        class="premium-input"
                        maxlength="8"
                        @keypress="allowOnlyNumbers"
                      />
                      <feather-icon
                        v-if="valid && supplier.cin && supplier.cin.length === 8"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- CIN or Matricule Fiscal validation message -->
              <b-col
                v-if="cinOrMatriculeError"
                cols="12"
              >
                <b-alert
                  variant="warning"
                  show
                  class="mb-2 py-1"
                >
                  <feather-icon
                    icon="AlertTriangleIcon"
                    size="16"
                    class="mr-1"
                  />
                  Veuillez renseigner au moins un des deux champs : Matricule fiscal ou CIN.
                </b-alert>
              </b-col>

              <!-- Address -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label-for="address"
                  description="Adresse complète du fournisseur (rue, ville, code postal)."
                  aria-required="true"
                >
                  <template #label>
                    Adresse postale <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Adresse"
                    rules="required"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-form-textarea
                        id="address"
                        v-model="supplier.address"
                        :state="errors.length > 0 ? false : (valid && supplier.address ? true : null)"
                        placeholder="Entrez l'adresse"
                        rows="3"
                        class="premium-input"
                      />
                      <feather-icon
                        v-if="valid && supplier.address"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon textarea-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>

          <b-card
            class="mb-3"
            aria-label="Informations de connexion du fournisseur"
          >
            <h5
              id="section-infos-connexion"
              class="mb-3 font-weight-bold"
            >
              <feather-icon
                icon="LockIcon"
                class="mr-1"
                aria-hidden="true"
              />
              Informations de connexion
            </h5>

            <b-row>
              <!-- Password -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label="Mot de passe *"
                  label-for="password"
                  description="Mot de passe d’accès à la plateforme (minimum 8 caractères)."
                  aria-required="true"
                >
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Mot de passe"
                    rules="required|min:8"
                    vid="password"
                    mode="eager"
                  >
                    <b-input-group class="input-group-merge position-relative">
                      <b-form-input
                        id="password"
                        v-model="supplier.password"
                        :type="showPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : (valid && supplier.password && supplier.password.length >= 8 ? true : null)"
                        placeholder="Entrez le mot de passe"
                        class="premium-input"
                        @input="onPasswordInput"
                      />
                      <feather-icon
                        v-if="valid && supplier.password && supplier.password.length >= 8"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon password-icon"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          @click="showPassword = !showPassword"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>

                    <!-- Password Strength Indicator -->
                    <div
                      v-if="supplier.password"
                      class="password-strength-wrapper mt-2"
                    >
                      <div class="d-flex justify-content-between mb-1">
                        <small class="strength-label">Force du mot de passe :</small>
                        <small
                          class="strength-text"
                          :class="passwordStrengthClass"
                        >{{ passwordStrengthText }}</small>
                      </div>
                      <b-progress
                        :value="passwordStrength"
                        :variant="passwordStrengthClass"
                        max="100"
                        height="6px"
                        class="premium-progress"
                      />
                    </div>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Confirm Password -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label-for="confirmPassword"
                  description="Saisissez à nouveau le mot de passe pour confirmation."
                  aria-required="true"
                >
                  <template #label>
                    Confirmer le mot de passe <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors }"
                    name="Confirmation du mot de passe"
                    rules="required|confirmed:password"
                    mode="eager"
                  >
                    <b-input-group class="input-group-merge position-relative">
                      <b-form-input
                        id="confirmPassword"
                        v-model="confirmPassword"
                        :type="showConfirmPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : (passwordsMatch && confirmPassword ? true : null)"
                        placeholder="Confirmez le mot de passe"
                        class="premium-input"
                        @input="onPasswordInput"
                      />
                      <feather-icon
                        v-if="passwordsMatch && confirmPassword"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon password-icon"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          @click="showConfirmPassword = !showConfirmPassword"
                        />
                      </b-input-group-append>
                    </b-input-group>

                    <!-- Real-time validation feedback -->
                    <div
                      v-if="confirmPassword"
                      class="mt-1"
                    >
                      <small
                        v-if="passwordsMatch && supplier.password"
                        class="text-success d-flex align-items-center validation-message-animate"
                      >
                        <feather-icon
                          icon="CheckCircleIcon"
                          size="14"
                          class="mr-50"
                        />
                        Les mots de passe correspondent
                      </small>
                      <small
                        v-else-if="supplier.password"
                        class="text-danger d-flex align-items-center validation-message-animate"
                      >
                        <feather-icon
                          icon="XCircleIcon"
                          size="14"
                          class="mr-50"
                        />
                        Les mots de passe ne correspondent pas
                      </small>
                      <small
                        v-else
                        class="text-warning d-flex align-items-center validation-message-animate"
                      >
                        <feather-icon
                          icon="AlertTriangleIcon"
                          size="14"
                          class="mr-50"
                        />
                        Veuillez d'abord saisir un mot de passe
                      </small>
                    </div>
                    <small
                      v-if="errors.length > 0 && !confirmPassword"
                      class="text-danger"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Generate Password Button -->
            <div class="text-center mb-2 mt-2">
              <PasswordGenerator
                :form-data="supplier"
                user-type="supplier"
                @generated="onPasswordGenerated"
              />
            </div>
          </b-card>

          <b-card
            class="mb-3"
            aria-label="Tarification logistique du fournisseur"
          >
            <h5
              id="section-tarification"
              class="mb-3 font-weight-bold"
            >
              <feather-icon
                icon="TruckIcon"
                class="mr-1"
                aria-hidden="true"
              />
              Informations de livraison
            </h5>

            <b-row>
              <!-- Repository -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label-for="repository"
                >
                  <template #label>
                    Dépôt <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Dépôt"
                    rules="required"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <v-select
                        id="repository"
                        v-model="supplier.repository"
                        :options="repositories"
                        label="name"
                        placeholder="Sélectionnez un dépôt"
                        :clearable="false"
                        :state="errors.length > 0 ? false : (valid && supplier.repository ? true : null)"
                        class="premium-select"
                        :class="{'is-valid': valid && supplier.repository, 'is-invalid': errors.length > 0}"
                      />
                      <feather-icon
                        v-if="valid && supplier.repository"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon select-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Weight price toggle -->
              <b-col
                md="6"
                class="d-flex align-items-center"
              >
                <b-form-group
                  label-class="form-label-premium"
                  class="mb-0"
                >
                  <b-form-checkbox
                    v-model="supplier.weight_price"
                    switch
                    class="custom-control-success"
                  >
                    <span class="font-weight-bold">Prix par poids</span>
                  </b-form-checkbox>
                  <small class="text-muted d-block mt-1">
                    Activer pour configurer les prix en fonction du poids
                  </small>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Standard pricing (when weight_price is false) -->
            <div
              v-if="!supplier.weight_price"
              class="mt-2"
            >
              <b-row>
                <b-col md="4">
                  <b-form-group
                    label-class="form-label-premium"
                    label="Prix de livraison (TND)"
                    label-for="delivery_price"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Prix de livraison"
                      :rules="!supplier.weight_price ? 'required|priceDecimal' : ''"
                    >
                      <b-input-group class="input-group-merge">
                        <b-form-input
                          id="delivery_price"
                          v-model="supplier.price_delivery"
                          class="premium-input"
                          :formatter="formatPrice"
                          type="text"
                          inputmode="decimal"
                          :state="errors.length > 0 ? false : null"
                          placeholder="0.000"
                          @keypress="allowOnlyDecimalNumbers"
                        />
                        <b-input-group-append is-text>
                          <span>TND</span>
                        </b-input-group-append>
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col md="4">
                  <b-form-group
                    label-class="form-label-premium"
                    label="Prix de retour (TND)"
                    label-for="return_price"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Prix de retour"
                      :rules="!supplier.weight_price ? 'required|priceDecimal' : ''"
                    >
                      <b-input-group class="input-group-merge">
                        <b-form-input
                          id="return_price"
                          v-model="supplier.price_return"
                          class="premium-input"
                          :formatter="formatPrice"
                          type="text"
                          inputmode="decimal"
                          :state="errors.length > 0 ? false : null"
                          placeholder="0.000"
                          @keypress="allowOnlyDecimalNumbers"
                        />
                        <b-input-group-append is-text>
                          <span>TND</span>
                        </b-input-group-append>
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col md="4">
                  <b-form-group
                    label-class="form-label-premium"
                    label="Prix d'échange (TND)"
                    label-for="exchange_price"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Prix d'échange"
                      rules="priceDecimal"
                    >
                      <b-input-group class="input-group-merge">
                        <b-form-input
                          id="exchange_price"
                          v-model="supplier.price_exchange"
                          class="premium-input"
                          :formatter="formatPrice"
                          type="text"
                          inputmode="decimal"
                          :state="errors.length > 0 ? false : null"
                          placeholder="0.000"
                          @keypress="allowOnlyDecimalNumbers"
                        />
                        <b-input-group-append is-text>
                          <span>TND</span>
                        </b-input-group-append>
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>            <!-- Weight-based pricing (when weight_price is true) -->
            <div
              v-else
              class="mt-3"
            >
              <div class="weight-pricing-header d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0 text-primary">
                  <feather-icon
                    icon="PackageIcon"
                    size="16"
                    class="mr-1"
                  />
                  Configuration des prix par poids
                </h6>
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  size="sm"
                  @click="repeateAgain"
                >
                  <feather-icon
                    icon="PlusIcon"
                    size="12"
                    class="mr-1"
                  />
                  <span>Ajouter une tranche</span>
                </b-button>
              </div>

              <b-alert
                v-if="weights.length === 0"
                variant="warning"
                show
                class="mb-3 d-flex justify-content-between align-items-center"
              >
                <div>
                  <feather-icon
                    icon="AlertTriangleIcon"
                    size="16"
                    class="mr-1"
                  />
                  Aucun poids disponible dans le système.
                </div>
                <b-button
                  variant="warning"
                  size="sm"
                  @click="openQuickCreateWeightModal"
                >
                  <feather-icon
                    icon="PlusIcon"
                    size="12"
                    class="mr-1"
                  />
                  <span>Créer un poids</span>
                </b-button>
              </b-alert>

              <div
                v-else
                class="weight-pricing-container"
              >
                <b-card
                  v-for="(item, index) in items"
                  :key="index"
                  class="weight-item-card mb-3"
                  no-body
                >
                  <b-card-body class="p-3">
                    <div class="d-flex justify-content-between align-items-start mb-2">
                      <h6 class="mb-0 text-secondary">
                        Poids {{ index + 1 }}
                      </h6>
                      <b-button
                        v-if="items.length > 1"
                        variant="outline-danger"
                        size="sm"
                        @click="removeItem(index)"
                      >
                        <feather-icon
                          icon="TrashIcon"
                          size="14"
                        />
                      </b-button>
                    </div>

                    <b-row>
                      <!-- Weight Selection -->
                      <b-col
                        md="12"
                        class="mb-3"
                      >
                        <b-form-group
                          label-class="form-label-premium"
                          label-for="weight"
                          class="mb-0"
                        >
                          <template #label>
                            <div class="d-flex justify-content-between align-items-center">
                              <span>Sélectionnez un poids <span class="text-danger">*</span></span>
                              <b-button
                                variant="link"
                                size="sm"
                                class="p-0 text-primary font-weight-normal"
                                @click="openQuickCreateWeightModal"
                              >
                                <feather-icon
                                  icon="PlusIcon"
                                  size="12"
                                  class="mr-1"
                                />
                                <span>Nouveau poids</span>
                              </b-button>
                            </div>
                          </template>
                          <validation-provider
                            #default="{ errors }"
                            name="Poids"
                            rules="required"
                          >
                            <v-select
                              v-model="item.weight"
                              :options="weights"
                              :get-option-label="option => option.weight"
                              :get-option-key="option => option.id"
                              placeholder="Choisissez un poids..."
                              :clearable="true"
                              :state="errors.length > 0 ? false : null"
                              class="weight-select"
                            >
                              <template #option="option">
                                <div class="weight-option">
                                  <div class="d-flex justify-content-between align-items-start">
                                    <div class="weight-info">
                                      <div class="font-weight-medium text-primary">
                                        {{ option.weight }}
                                      </div>
                                      <div class="weight-range">
                                        <small class="text-muted">
                                          <span v-if="option.min && option.max">
                                            {{ option.min }} - {{ option.max }} {{ option.unit }}
                                          </span>
                                          <span v-else-if="option.min">
                                            À partir de {{ option.min }} {{ option.unit }}
                                          </span>
                                          <span v-else-if="option.max">
                                            Jusqu'à {{ option.max }} {{ option.unit }}
                                          </span>
                                          <span v-else>
                                            {{ option.unit }}
                                          </span>
                                        </small>
                                      </div>
                                      <div
                                        v-if="option.description"
                                        class="weight-description mt-1"
                                      >
                                        <small class="text-muted">{{ option.description }}                            </small>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </template>
                              <template #selected-option="option">
                                <div class="d-flex justify-content-between align-items-center">
                                  <span class="font-weight-medium">{{ option.weight }}</span>
                                  <small class="text-muted ml-2">
                                    <span v-if="option.min && option.max">
                                      ({{ option.min }}-{{ option.max }} {{ option.unit }})
                                    </span>
                                    <span v-else>
                                      ({{ option.unit }})
                                    </span>
                                  </small>
                                </div>
                              </template>
                            </v-select>
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-form-group>
                      </b-col>

                      <!-- Delivery Price -->
                      <b-col md="6">
                        <b-form-group
                          label-class="form-label-premium"
                          label="Prix de livraison"
                          label-for="delivery_price"
                          class="mb-0"
                        >
                          <validation-provider
                            #default="{ errors }"
                            name="Prix de livraison"
                            rules="required|priceDecimal"
                          >
                            <b-input-group class="input-group-merge">
                              <b-form-input
                                v-model="item.delivery_price"
                                class="premium-input"
                                :formatter="formatPrice"
                                type="text"
                                inputmode="decimal"
                                placeholder="0.000"
                                :state="errors.length > 0 ? false : null"
                                @keypress="allowOnlyDecimalNumbers"
                              />
                              <b-input-group-append is-text>
                                <span class="text-muted">TND</span>
                              </b-input-group-append>
                            </b-input-group>
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-form-group>
                      </b-col>

                      <!-- Return Price -->
                      <b-col md="6">
                        <b-form-group
                          label-class="form-label-premium"
                          label="Prix de retour"
                          label-for="return_price"
                          class="mb-0"
                        >
                          <validation-provider
                            #default="{ errors }"
                            name="Prix de retour"
                            rules="required|priceDecimal"
                          >
                            <b-input-group class="input-group-merge">
                              <b-form-input
                                v-model="item.return_price"
                                class="premium-input"
                                :formatter="formatPrice"
                                type="text"
                                inputmode="decimal"
                                placeholder="0.000"
                                :state="errors.length > 0 ? false : null"
                                @keypress="allowOnlyDecimalNumbers"
                              />
                              <b-input-group-append is-text>
                                <span class="text-muted">TND</span>
                              </b-input-group-append>
                            </b-input-group>
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-form-group>
                      </b-col>
                    </b-row>

                    <!-- Selected Weight Info -->
                    <div
                      v-if="item.weight"
                      class="mt-3 p-3 bg-light rounded weight-details-card"
                    >
                      <div class="weight-details">
                        <h6 class="mb-2 text-primary">
                          <feather-icon
                            icon="PackageIcon"
                            size="14"
                            class="mr-1"
                          />
                          {{ item.weight.weight }}
                        </h6>

                        <div class="weight-specs">
                          <div class="d-flex align-items-center mb-1">
                            <feather-icon
                              icon="ScaleIcon"
                              size="12"
                              class="mr-1 text-muted"
                            />
                            <small class="text-muted">
                              <strong>Plage de poids:</strong>
                              <span v-if="item.weight.min && item.weight.max">
                                {{ item.weight.min }} - {{ item.weight.max }} {{ item.weight.unit }}
                              </span>
                              <span v-else-if="item.weight.min">
                                À partir de {{ item.weight.min }} {{ item.weight.unit }}
                              </span>
                              <span v-else-if="item.weight.max">
                                Jusqu'à {{ item.weight.max }} {{ item.weight.unit }}
                              </span>
                              <span v-else>
                                Unité: {{ item.weight.unit }}
                              </span>
                            </small>
                          </div>

                          <div
                            v-if="item.weight.description"
                            class="d-flex align-items-start"
                          >
                            <feather-icon
                              icon="InfoIcon"
                              size="12"
                              class="mr-1 text-muted mt-1"
                            />
                            <small class="text-muted">
                              <strong>Description:</strong> {{ item.weight.description }}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </b-card-body>
                </b-card>
              </div></div></b-card>

          <div
            v-if="items.length === 0"
            class="text-center py-4"
          >
            <feather-icon
              icon="PackageIcon"
              size="48"
              class="text-muted mb-2"
            />
            <p class="text-muted">
              Aucune configuration de prix par poids ajoutée.
            </p>
            <b-button
              variant="outline-primary"
              @click="repeateAgain"
            >
              <feather-icon
                icon="PlusIcon"
                size="14"
                class="mr-1"
              />
              Ajouter le premier poids
            </b-button>              </div>

          <div class="d-flex justify-content-between mt-4">
            <b-button
              variant="outline-secondary"
              :to="{ name: 'supplier-list' }"
            >
              <feather-icon
                icon="XIcon"
                class="mr-1"
              />
              <span>Annuler</span>
            </b-button>

            <span
              id="supplier-create-save-btn-wrapper"
              class="d-inline-block"
            >
              <b-button
                variant="primary"
                type="submit"
                :class="{ 'not-allowed': !isSupplierValid || isLoading }"
                :disabled="!isSupplierValid || isLoading"
                aria-label="Enregistrer le nouveau fournisseur"
              >
                <feather-icon
                  icon="SaveIcon"
                  class="mr-1"
                  aria-hidden="true"
                />
                <span>Enregistrer</span>
              </b-button>
              <b-tooltip
                v-if="!isSupplierValid || isLoading"
                target="supplier-create-save-btn-wrapper"
                triggers="hover focus"
                placement="top"
              >
                {{ saveButtonTooltipMessage }}
              </b-tooltip>
            </span>
          </div>
        </ValidationObserver>
      </b-form>
    </b-overlay>
    <quick-create-weight-modal
      ref="quickCreateWeightModal"
      @weight-created="handleWeightCreated"
    />
  </b-card-code>
</template>

<script>
// Import necessary components
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PasswordGenerator from '@core/components/password/PasswordGenerator.vue'
import BCardCode from '@core/components/b-card-code'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import Ripple from 'vue-ripple-directive'
import { heightTransition } from '@core/mixins/ui/transition'
import { required } from '@validations'
import {
  BForm, BRow, BCol, BFormGroup, BFormInput, BButton, BOverlay,
  BCard, BInputGroup, BInputGroupAppend, BFormCheckbox, BAlert, BCardBody, BFormTextarea,
  BProgress, BTooltip,
} from 'bootstrap-vue'
import QuickCreateWeightModal from '@/components/modals/QuickCreateWeightModal.vue'

export default {
  components: {
    BCardCode,
    ValidationProvider,
    ValidationObserver,
    PasswordGenerator,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BOverlay,
    BCard,
    BInputGroup,
    BInputGroupAppend,
    BFormCheckbox,
    BAlert,
    BCardBody,
    BProgress,
    BTooltip,
    vSelect,
    QuickCreateWeightModal,
  },

  directives: {
    Ripple,
  },

  mixins: [heightTransition],

  data() {
    return {
      items: [
        {
          delivery_price: '',
          return_price: '',
          weight: '',
        },
      ],
      weights: [],
      weights_orders: [],
      confirmPassword: '',
      repositories: [],
      supplier: {
        price_return: 0,
        price_delivery: 0,
        price_exchange: 0,
        username: '',
        is_an_shipment_company: false,
        email: '',
        password: '',
        address: '',
        repository: '',
        phone: '',
        phone2: '',
        page_name: '',
        website_url: '',
        payment_type: 'espece',
        rib: '',
        invoice_generation_days: [],
        tax_identification_number: '',
        cin: '',
        weight_price: false,
      },
      paymentTypeOptions: [
        { label: 'Espèce', value: 'espece' },
        { label: 'Virement', value: 'virement' },
      ],
      weekdayOptions: [
        { label: 'Lundi', value: 'monday' },
        { label: 'Mardi', value: 'tuesday' },
        { label: 'Mercredi', value: 'wednesday' },
        { label: 'Jeudi', value: 'thursday' },
        { label: 'Vendredi', value: 'friday' },
        { label: 'Samedi', value: 'saturday' },
        { label: 'Dimanche', value: 'sunday' },
      ],
      required,
      isLoading: false,
      showPassword: false,
      showConfirmPassword: false,
    }
  },

  computed: {
    companyDomain() {
      return process.env.VUE_APP_COMPANY_EMAIL_DOMAIN || null
    },

    emailGenerationTooltip() {
      if (!this.companyDomain) {
        return 'Erreur: Variable d\'environnement VUE_APP_COMPANY_EMAIL_DOMAIN non définie'
      }
      if (this.supplier.username && this.supplier.username.trim().length > 0) {
        return `Générer un email par défaut à partir du nom (@${this.companyDomain}). Modifiable si l'expéditeur possède son propre email.`
      }
      return 'Veuillez d\'abord saisir le nom de l\'expéditeur'
    },

    cinOrMatriculeError() {
      // Returns true if neither CIN nor matricule fiscal is filled
      const hasCin = this.supplier.cin && this.supplier.cin.trim() !== ''
      const hasMatricule = this.supplier.tax_identification_number && this.supplier.tax_identification_number.trim() !== ''
      return !hasCin && !hasMatricule
    },
    passwordsMatch() {
      return (
        this.supplier.password
        && this.confirmPassword
        && this.supplier.password === this.confirmPassword
      )
    },
    passwordStrength() {
      let strength = 0
      const { password } = this.supplier

      if (!password) return 0

      if (password.length >= 8) strength += 25
      if (/[A-Z]/.test(password)) strength += 25
      if (/[0-9]/.test(password)) strength += 25
      if (/[^A-Za-z0-9]/.test(password)) strength += 25

      return strength
    },
    passwordStrengthClass() {
      if (this.passwordStrength <= 25) return 'danger'
      if (this.passwordStrength <= 50) return 'warning'
      if (this.passwordStrength <= 75) return 'info'
      return 'success'
    },
    passwordStrengthText() {
      if (this.passwordStrength <= 25) return 'Faible'
      if (this.passwordStrength <= 50) return 'Moyen'
      if (this.passwordStrength <= 75) return 'Bon'
      return 'Fort'
    },
    saveButtonTooltipMessage() {
      if (this.isLoading) return 'Enregistrement en cours...'

      const missingGeneral = []
      if (!this.supplier.username || !this.supplier.username.trim()) missingGeneral.push('Nom')
      if (!this.supplier.email || !this.supplier.email.trim()) missingGeneral.push('Email')
      if (!this.supplier.phone || !this.supplier.phone.trim()) missingGeneral.push('Téléphone')
      if (!this.supplier.address || !this.supplier.address.trim()) missingGeneral.push('Adresse')

      const missingIdentification = this.cinOrMatriculeError

      const missingDelivery = []
      if (!this.supplier.repository) missingDelivery.push('Dépôt')
      if (!this.supplier.weight_price) {
        if (this.supplier.price_delivery === '' || this.supplier.price_delivery === null || this.supplier.price_delivery < 0) {
          missingDelivery.push('Prix de livraison')
        }
        if (this.supplier.price_return === '' || this.supplier.price_return === null || this.supplier.price_return < 0) {
          missingDelivery.push('Prix de retour')
        }
      } else {
        const hasValidWeight = this.items.some(item => item.weight && item.delivery_price >= 0 && item.return_price >= 0)
        if (!hasValidWeight) missingDelivery.push('Prix par poids')
      }

      const missingPassword = []
      if (!this.supplier.password) {
        missingPassword.push('Mot de passe')
      } else if (this.supplier.password.length < 8) {
        return 'Le mot de passe doit comporter au moins 8 caractères.'
      }

      if (!this.confirmPassword) {
        missingPassword.push('Confirmation du mot de passe')
      } else if (this.supplier.password && !this.passwordsMatch) {
        return 'Les mots de passe ne correspondent pas.'
      }

      const allMissing = []
      if (missingGeneral.length > 0) allMissing.push(...missingGeneral)
      if (missingIdentification) allMissing.push('Matricule Fiscal ou CIN')
      if (missingDelivery.length > 0) allMissing.push(...missingDelivery)
      if (missingPassword.length > 0) allMissing.push(...missingPassword)

      if (allMissing.length > 0) {
        if (allMissing.length === 1) {
          return `Veuillez renseigner le champ : ${allMissing[0]}.`
        }
        if (allMissing.length <= 3) {
          return `Veuillez remplir les champs obligatoires : ${allMissing.join(', ')}.`
        }
        return `Veuillez remplir les champs obligatoires manquants (${allMissing.length} champs).`
      }

      return 'Veuillez corriger les erreurs dans le formulaire.'
    },
    isSupplierValid() {
      const basicFieldsValid = (
        this.supplier.email !== ''
        && this.supplier.password !== ''
        && this.confirmPassword !== ''
        && this.supplier.password.length >= 8
        && this.passwordsMatch
        && this.supplier.username !== ''
        && this.supplier.phone !== ''
        && this.supplier.address !== ''
        && this.supplier.repository !== ''
      )

      if (!basicFieldsValid) return false

      // Check if at least one of CIN or matricule fiscal is filled
      if (this.cinOrMatriculeError) return false

      // If weight_price is false, validate standard pricing
      if (!this.supplier.weight_price) {
        return this.supplier.price_delivery >= 0 && this.supplier.price_return >= 0
      }

      // If weight_price is true, validate weight-based pricing
      return this.items.some(item => item.weight
        && item.delivery_price >= 0
        && item.return_price >= 0)
    },
  },

  watch: {
    'supplier.weight_price': function handleWeightPriceChange(newVal) {
      // Reset standard pricing when switching to weight-based pricing
      if (newVal) {
        this.supplier.price_delivery = 0
        this.supplier.price_return = 0
      }
    },
    'supplier.payment_type': function handlePaymentTypeChange(newVal) {
      if (newVal !== 'virement') {
        this.supplier.rib = ''
      }
    },
  },

  created() {
    // Security check - redirect if superadmin (cannot create suppliers)
    const userRole = this.$store.state.user?.role
    if (userRole === 'superadmin') {
      this.$toast.warning('Les super administrateurs ne peuvent pas créer de expéditeurs')
      this.$router.push({ name: 'supplier-list' })
      return
    }

    window.addEventListener('resize', this.initTrHeight)
    this.getRepositories()
    this.getWeights()
  },

  destroyed() {
    window.removeEventListener('resize', this.initTrHeight)
  },

  methods: {
    onPasswordInput() {
      this.$forceUpdate()
    },
    getConfirmPasswordState(errors) {
      if (errors.length > 0) return false
      if (!this.confirmPassword) return null
      return this.passwordsMatch
    },
    onPasswordGenerated(generatedPassword) {
      this.supplier.password = generatedPassword
      this.confirmPassword = generatedPassword
      this.showPassword = true
      this.showConfirmPassword = true
      this.$forceUpdate()
    },
    openQuickCreateWeightModal() {
      if (this.$refs.quickCreateWeightModal) {
        this.$refs.quickCreateWeightModal.show()
      }
    },
    async handleWeightCreated(newWeight) {
      await this.getWeights()
      // Find an empty weight row or create a new row pre-selected with newWeight
      const emptyItem = this.items.find(item => !item.weight)
      if (emptyItem) {
        emptyItem.weight = newWeight
      } else {
        this.items.push({
          weight: newWeight,
          delivery_price: '',
          return_price: '',
        })
      }
    },
    isDaySelected(dayValue) {
      return this.supplier.invoice_generation_days && this.supplier.invoice_generation_days.includes(dayValue)
    },
    toggleDay(dayValue) {
      if (!this.supplier.invoice_generation_days) {
        this.supplier.invoice_generation_days = []
      }
      const index = this.supplier.invoice_generation_days.indexOf(dayValue)
      if (index > -1) {
        this.supplier.invoice_generation_days.splice(index, 1)
      } else {
        this.supplier.invoice_generation_days.push(dayValue)
      }
    },
    getWeekdayLabel(dayValue) {
      const option = this.weekdayOptions.find(opt => opt.value === dayValue)
      return option ? option.label : dayValue
    },
    async getWeights() {
      this.isLoading = true
      try {
        const { data } = await axios.get('/api/weights/', {
          params: {
            is_active: true, // Only fetch active weights
          },
        })
        // Handle both paginated and direct array responses
        let weightsArray = []
        if (data && data.results && Array.isArray(data.results)) {
          weightsArray = data.results
        } else if (Array.isArray(data)) {
          weightsArray = data
        } else {
          weightsArray = []
        }
        // Filter only active weights on frontend as well (double check)
        this.weights = weightsArray.filter(weight => weight.is_active)
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des poids')
        this.weights = [] // Ensure weights is always an array
      } finally {
        this.isLoading = false
      }
    },

    removeItem(index) {
      this.items.splice(index, 1)
      if (this.$refs.row && this.$refs.row[0]) {
        this.trTrimHeight(this.$refs.row[0].offsetHeight)
      }
    },

    initTrHeight() {
      this.trSetHeight(null)
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.trSetHeight(this.$refs.form.scrollHeight)
        }
      })
    },

    repeateAgain() {
      this.items.push({
        weight: '',
        delivery_price: '',
        return_price: '',
      })

      this.$nextTick(() => {
        if (this.$refs.row && this.$refs.row[0]) {
          this.trAddHeight(this.$refs.row[0].offsetHeight)
        }
      })
    },

    async getRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des dépôts')
      }
    },

    async validateAndSubmit() {
      const success = await this.$refs.simpleRules.validate()
      if (!success || !this.isSupplierValid) {
        this.showToast('danger', 'top-center', 'Veuillez corriger les erreurs dans le formulaire')
        return
      }

      await this.addSupplier()
    },

    async addSupplier() {
      if (this.supplier.weight_price === false) {
        this.weights_orders = []
      } else {
        this.weights_orders = this.items.filter(x => x.weight).map(x => x.weight.id)
      }
      const parsePrice = val => {
        if (val === null || val === undefined || val === '') return null
        const normalized = String(val).replace(',', '.')
        const num = parseFloat(normalized)
        return Number.isNaN(num) ? val : num
      }

      const formattedPrices = this.supplier.weight_price
        ? this.items.map(item => ({
          ...item,
          delivery_price: parsePrice(item.delivery_price),
          return_price: parsePrice(item.return_price),
        }))
        : []

      this.isLoading = true
      try {
        const payload = {
          weight_price: this.supplier.weight_price,
          email: this.supplier.email,
          password: this.supplier.password,
          username: this.supplier.username,
          is_an_shipment_company: this.supplier.is_an_shipment_company,
          phone: this.supplier.phone,
          phone2: this.supplier.phone2,
          tax_identification_number: this.supplier.tax_identification_number,
          cin: this.supplier.cin,
          address: this.supplier.address,
          page_name: this.supplier.page_name,
          website_url: this.supplier.website_url,
          payment_type: this.supplier.payment_type,
          rib: this.supplier.payment_type === 'virement' ? this.supplier.rib : '',
          invoice_generation_days: this.supplier.invoice_generation_days || [],
          repository: this.supplier.repository.id,
          prices: formattedPrices,
          weights: this.weights_orders,
          price_exchange: parsePrice(this.supplier.price_exchange),
        }
        // Only include standard pricing when weight_price is false
        if (!this.supplier.weight_price) {
          payload.price_return = parsePrice(this.supplier.price_return)
          payload.price_delivery = parsePrice(this.supplier.price_delivery)
        }
        await axios.post('/api/auth/register/supplier/', payload)
        this.showToast('success', 'top-center', 'Fournisseur ajouté avec succès')
        this.$router.push('/suppliers')
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating supplier:', error)

        // Extract meaningful error message from backend response
        let errorMessage = 'Erreur lors de la création du fournisseur'

        if (error.response && error.response.data) {
          const errorData = error.response.data

          if (typeof errorData === 'object' && errorData !== null) {
            const messages = []
            // Handle both { field: [error] } and { errors: { field: [error] } }
            const fieldsToProcess = (errorData.errors && typeof errorData.errors === 'object')
              ? errorData.errors
              : errorData

            Object.keys(fieldsToProcess).forEach(field => {
              const fieldError = fieldsToProcess[field]
              const rawError = Array.isArray(fieldError) ? fieldError[0] : fieldError
              if (typeof rawError === 'string') {
                if (rawError.includes('existe') || rawError.includes('already exists')) {
                  if (field === 'phone') messages.push('Ce numéro de téléphone est déjà utilisé.')
                  else if (field === 'email') messages.push('Cette adresse email est déjà utilisée.')
                  else if (field === 'username') messages.push('Ce nom est déjà utilisé.')
                  else messages.push(`Le champ ${field} est déjà utilisé.`)
                } else {
                  messages.push(rawError)
                }
              }
            })

            if (messages.length > 0) {
              errorMessage = messages.join(' ')
            } else if (errorData.message) {
              errorMessage = errorData.message
            } else if (errorData.detail) {
              errorMessage = errorData.detail
            }
          } else if (typeof errorData === 'string') {
            errorMessage = errorData
          }
        }

        this.showToast('danger', 'top-center', errorMessage)
      } finally {
        this.isLoading = false
      }
    },

    showToast(variant, position, text) {
      const variantMeta = {
        success: { title: 'Succès', icon: 'CheckCircleIcon' },
        info: { title: 'Information', icon: 'InfoIcon' },
        warning: { title: 'Attention', icon: 'AlertTriangleIcon' },
        danger: { title: 'Erreur', icon: 'AlertCircleIcon' },
      }
      const meta = variantMeta[variant] || variantMeta.danger
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: meta.title,
            icon: meta.icon,
            text,
            variant,
          },
        },
        { position },
      )
    },

    formatPhone(e) {
      return String(e).substring(0, 8)
    },
    generateEmail() {
      if (!this.supplier.username) {
        this.showToast('warning', 'top-center', 'Veuillez d\'abord saisir le nom du expéditeur')
        return
      }

      // Normalize username: lowercase, remove special chars, replace spaces with dots
      const emailName = this.supplier.username
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove accents
        .replace(/[^a-z0-9\s]/g, '') // Remove special characters
        .trim()
        .replace(/\s+/g, '.') // Replace spaces with dots

      if (!emailName) {
        this.showToast('warning', 'top-center', 'Le nom saisi ne contient aucun caractère utilisable pour générer un email.')
        return
      }

      const domain = process.env.VUE_APP_COMPANY_EMAIL_DOMAIN
      if (!domain) {
        this.showToast('error', 'top-center', 'Erreur: Variable d\'environnement VUE_APP_COMPANY_EMAIL_DOMAIN non définie')
        return
      }
      const baseEmail = `${emailName}@${domain}`

      // Best-effort uniqueness check against existing suppliers.
      this.resolveUniqueEmail(emailName, domain)
        .then(({ email, suffix, skipped }) => {
          this.supplier.email = email
          if (skipped) {
            // Verification failed; we assigned the base email and let the backend catch any duplicate at save time.
            this.showToast('warning', 'top-center', `Email généré : ${email} (unicité non vérifiée — vérifié à l'enregistrement)`)
          } else if (suffix > 0) {
            this.showToast('success', 'top-center', `Email généré : ${email} (suffixe .${suffix} ajouté pour éviter un doublon)`)
          } else {
            this.showToast('success', 'top-center', `Email généré avec succès (${email})`)
          }
        })
        .catch(() => {
          // Network/unknown error — still assign the base email so the user is never blocked.
          this.supplier.email = baseEmail
          this.showToast('warning', 'top-center', `Email généré : ${baseEmail} (unicité non vérifiée — vérifié à l'enregistrement)`)
        })
    },

    /**
     * Try base email <emailName>@<domain>; if it collides with an existing supplier,
     * try <emailName>.1@<domain>, <emailName>.2@<domain>, ... up to MAX_ATTEMPTS.
     * Resolves with { email, suffix, skipped } where `skipped` indicates the uniqueness
     * check could not be performed (network error or unexpected response).
     */
    async resolveUniqueEmail(emailName, domain) {
      const MAX_ATTEMPTS = 50
      const candidates = []
      for (let i = 0; i < MAX_ATTEMPTS; i += 1) {
        const local = i === 0 ? emailName : `${emailName}.${i}`
        const email = `${local}@${domain}`
        candidates.push({ email, suffix: i })
      }

      // Single paginated lookup limited to the local-part + domain to catch collisions.
      try {
        const { data } = await axios.get('/api/auth/users/suppliers/', {
          params: { search: `${emailName}@${domain}`, page_size: MAX_ATTEMPTS + 10 },
        })
        const results = data && Array.isArray(data.results) ? data.results : []
        const existingEmails = new Set(
          results
            .map(s => (s.email || '').trim().toLowerCase())
            .filter(Boolean),
        )
        // If the search endpoint uses a partial match, results may include suppliers whose username matches
        // but whose email differs. We only treat an exact-email match as a collision.
        const free = candidates.find(c => !existingEmails.has(c.email.toLowerCase()))
        if (free) {
          return { email: free.email, suffix: free.suffix, skipped: false }
        }
        // Extremely unlikely (50 collisions). Fall back to the last candidate, flagged as unverified.
        return { email: candidates[candidates.length - 1].email, suffix: MAX_ATTEMPTS - 1, skipped: true }
      } catch (error) {
        // Search request failed — return the base candidate, flagged as unverified.
        return { email: candidates[0].email, suffix: 0, skipped: true }
      }
    },
    allowOnlyNumbers(event) {
      const { charCode, target } = event
      // Only allow digits
      if (charCode < 48 || charCode > 57) {
        event.preventDefault()
      }
      // Prevent more than 8 digits
      if (target.id === 'phone' && this.supplier.phone && this.supplier.phone.length >= 8) {
        event.preventDefault()
      }
      if (target.id === 'phone2' && this.supplier.phone2 && this.supplier.phone2.length >= 8) {
        event.preventDefault()
      }
    },
    allowOnlyDecimalNumbers(event) {
      const { key, charCode, target } = event
      const currentValue = (target && target.value) || ''

      const isDigit = charCode >= 48 && charCode <= 57
      const isDecimalSeparator = key === '.' || key === ',' || charCode === 46 || charCode === 44

      if (!isDigit && !isDecimalSeparator) {
        event.preventDefault()
        return
      }

      if (isDecimalSeparator && (currentValue.includes('.') || currentValue.includes(','))) {
        event.preventDefault()
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return value
      let cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '')
      const firstSepIndex = cleaned.indexOf('.')
      if (firstSepIndex !== -1) {
        const main = cleaned.slice(0, firstSepIndex + 1)
        const rest = cleaned.slice(firstSepIndex + 1).replace(/\./g, '')
        cleaned = main + rest
      }
      return cleaned
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

// Fix for v-select display issues on initial load
.vs__dropdown-toggle {
  display: flex !important;
  align-items: center !important;
  min-height: 38px !important;
  border: 1px solid #d8d6de !important;
  border-radius: 0.357rem !important;
  background-color: #fff !important;
}

.vs__selected-options {
  display: flex !important;
  flex-wrap: wrap !important;
  padding: 0 !important;
}

.vs__search,
.vs__search:focus {
  margin: 4px 0 0 0 !important;
  padding: 7px 10px !important;
  background: none !important;
  border: none !important;
  outline: none !important;
  color: #6e6b7b !important;
  flex-grow: 1 !important;
}

.vs__selected {
  margin: 4px 2px 0px 2px !important;
  padding: 0 0.25em !important;
  color: #6e6b7b !important;
  line-height: 1.4 !important;
  z-index: 0 !important;
}

.vs__actions {
  display: flex !important;
  align-items: center !important;
  padding: 4px 6px 0 3px !important;
}

.vs__clear,
.vs__open-indicator {
  cursor: pointer !important;
  margin: 0 !important;
}

// Premium Payment Configuration Card Styles
.payment-configuration-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2eefa;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(13, 67, 153, 0.04);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(13, 67, 153, 0.08);
  }

  .card-body {
    padding: 1.5rem;
  }
}

.payment-icon-wrapper {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e2eefa 0%, #f2f7fc 100%);
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(13, 67, 153, 0.1);
}

.payment-days-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.25rem;
  transition: all 0.2s ease;

  label {
    color: #334155;
    letter-spacing: 0.3px;
  }
}

.days-picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.day-picker-item {
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem 0.75rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    border-color: #9b1c22;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(13, 67, 153, 0.15);
  }

  &.selected {
    background: linear-gradient(135deg, #e2eefa 0%, #f2f7fc 100%);
    border-color: #9b1c22;
    border-width: 2px;

    .day-label {
      color: #9b1c22;
      font-weight: 700;
    }

    &:hover {
      box-shadow: 0 6px 16px rgba(13, 67, 153, 0.25);
    }
  }

  .day-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 32px;
  }

  .day-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #64748b;
    transition: all 0.2s ease;
  }
}

.selected-summary {
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  display: inline-flex;
  align-items: center;
}

.premium-form-group {
  label {
    color: #334155;
    font-weight: 600;
    letter-spacing: 0.3px;
    margin-bottom: 0.5rem;
  }
}

.custom-control-success .custom-control-input:checked ~ .custom-control-label::before {
  border-color: #28c76f;
  background-color: #28c76f;
}

.weight-pricing-container {
  .weight-item-card {
    border: 1px solid #e9ecef;
    transition: all 0.2s ease;

    &:hover {
      border-color: #9b1c22;
      box-shadow: 0 2px 8px rgba(115, 103, 240, 0.1);
    }
  }  .weight-select {
    .vs__dropdown-toggle {
      border: 1px solid #d8d6de;
      border-radius: 0.357rem;
      min-height: 38px;
    }

    .vs__selected {
      color: #6e6b7b;
      font-weight: 500;
    }

    .vs__dropdown-option {
      padding: 12px;

      &--highlight {
        background: #9b1c22;
        color: white;

        .weight-option .weight-info .font-weight-medium {
          color: white !important;
        }
      }
    }

    .weight-option {
      .weight-info {
        .font-weight-medium {
          color: #9b1c22;
          font-size: 14px;
        }

        .weight-range {
          margin-top: 2px;
        }

        .weight-description {
          max-width: 250px;
          line-height: 1.3;
        }
      }
    }
  }
}

.weight-details-card {
  border: 1px solid #e3e6f0;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);

  .weight-details {
    h6 {
      color: #9b1c22;
      margin-bottom: 8px;
    }

    .weight-specs {
      .d-flex {
        margin-bottom: 4px;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
}

.weight-pricing-header {
  h6 {
    color: #9b1c22;
    font-weight: 600;
  }
}

// Enhanced input styling
.input-group-merge {
  .form-control {
    border-right: 0;

    &:focus {
      border-color: #9b1c22;
      box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
    }
  }

  .input-group-append {
    .input-group-text {
      background-color: #f8f8f8;
      border-left: 0;
      color: #6e6b7b;
    }
  }
}

.btn.not-allowed,
.btn:disabled {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important;
}

// Premium Input Styling with Real-time Validation
.premium-input {
  transition: all 0.3s ease;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;

  &:focus {
    border-color: #9b1c22;
    box-shadow: 0 0 0 3px rgba(13, 67, 153, 0.15);
  }

  &.is-valid {
    border-color: #28c76f !important;
    background-color: #f0fdf4;
    padding-right: 3rem;

    &:focus {
      border-color: #28c76f !important;
      box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.15);
    }
  }

  &.is-invalid {
    border-color: #ea5455 !important;
    background-color: #fef2f2;

    &:focus {
      border-color: #ea5455 !important;
      box-shadow: 0 0 0 3px rgba(234, 84, 85, 0.15);
    }
  }
}

.input-success-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #28c76f;
  pointer-events: none;
  z-index: 5;
  animation: checkFadeIn 0.3s ease;

  &.textarea-icon {
    top: 1rem;
    transform: none;
  }

  &.select-icon {
    right: 3rem;
  }

  &.password-icon {
    right: 3.5rem;
  }
}

@keyframes checkFadeIn {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.5);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

.validation-message-animate {
  display: inline-flex;
  align-items: center;
  animation: fadeInDown 0.3s ease;
  margin-top: 0.25rem;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// V-Select Real-time Validation
.premium-select {
  &.is-valid .vs__dropdown-toggle {
    border: 2px solid #28c76f !important;
    background-color: #f0fdf4;
  }

  &.is-invalid .vs__dropdown-toggle {
    border: 2px solid #ea5455 !important;
    background-color: #fef2f2;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
